import React, { useEffect, useState } from "react";
import { X, Share } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useCity } from "../context/CityContext";

const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

const DISMISS_DAYS = 30;
const HIDDEN_PREFIXES = ["/fleurs/", "/resines/", "/puffs/", "/chicha/", "/produit/", "/panier", "/paiement", "/commande-envoyee", "/admin", "/vendeur", "/login", "/register"];

const countVisit = () => {
  if (sessionStorage.getItem("stuff_visit_counted")) return Number(localStorage.getItem("stuff_visits") || 1);
  const n = Number(localStorage.getItem("stuff_visits") || 0) + 1;
  localStorage.setItem("stuff_visits", String(n));
  sessionStorage.setItem("stuff_visit_counted", "1");
  return n;
};
const dismissedRecently = () => {
  const at = Number(localStorage.getItem("pwa_prompt_dismissed_at") || 0);
  return localStorage.getItem("pwa_prompt_dismissed") === "1" && (!at || Date.now() - at < DISMISS_DAYS * 86400000);
};

export const InstallPrompt = () => {
  const { pathname } = useLocation();
  const { modalOpen } = useCity();
  const [deferred, setDeferred] = useState(null);
  const [showIos, setShowIos] = useState(false);
  const [hidden, setHidden] = useState(dismissedRecently);
  const [eligible, setEligible] = useState(false);

  useEffect(() => {
    const visits = countVisit();
    const check = () => {
      const ageOk = localStorage.getItem("stuff_age_ok") === "1";
      const cookieOk = localStorage.getItem("stuff_cookie_ok") === "1";
      const ordered = localStorage.getItem("stuff_ordered") === "1";
      setEligible(ageOk && cookieOk && (ordered || visits >= 2));
    };
    check();
    const timer = setInterval(check, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setDeferred(e); };
    window.addEventListener("beforeinstallprompt", handler);
    if (isIos() && !isStandalone()) {
      const t = setTimeout(() => setShowIos(true), 2500);
      return () => { window.removeEventListener("beforeinstallprompt", handler); clearTimeout(t); };
    }
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const dismiss = () => { localStorage.setItem("pwa_prompt_dismissed", "1"); localStorage.setItem("pwa_prompt_dismissed_at", String(Date.now())); setHidden(true); };

  if (hidden || isStandalone() || !eligible || modalOpen || HIDDEN_PREFIXES.some((p) => pathname.startsWith(p))) return null;

  if (deferred) {
    const install = async () => {
      deferred.prompt();
      await deferred.userChoice;
      setDeferred(null);
    };
    return (
      <div data-testid="pwa-install-banner" className="fixed bottom-4 left-4 right-4 sm:left-auto sm:w-96 z-50 bg-ink-surface border border-neon-green p-4 flex items-center gap-3 shadow-[0_0_20px_rgba(157,255,0,0.25)]">
        <img src="/icon-192.png" alt="STUFF CBD" className="h-11 w-11 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-white text-sm font-bold">Installe l'app STUFF CBD</p>
          <p className="text-zinc-500 text-xs">Sur ton écran d'accueil, comme une vraie app.</p>
        </div>
        <button data-testid="pwa-install-btn" onClick={install} className="shrink-0 bg-neon-green text-black text-xs uppercase font-bold px-3 py-2">Installer</button>
        <button data-testid="pwa-install-dismiss" onClick={dismiss} className="shrink-0 text-zinc-500 hover:text-white"><X className="h-4 w-4" /></button>
      </div>
    );
  }

  if (showIos) {
    return (
      <div data-testid="pwa-ios-banner" className="fixed bottom-4 left-4 right-4 sm:left-auto sm:w-96 z-50 bg-ink-surface border border-neon-green p-4 flex items-start gap-3 shadow-[0_0_20px_rgba(157,255,0,0.25)]">
        <img src="/icon-192.png" alt="STUFF CBD" className="h-11 w-11 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-white text-sm font-bold mb-1">Installe l'app STUFF CBD 📲</p>
          <p className="text-zinc-400 text-xs leading-relaxed">
            Appuie sur <Share className="inline h-3.5 w-3.5 text-neon-green -mt-0.5" /> <span className="text-neon-green font-semibold">Partager</span> en bas de Safari, puis choisis <span className="text-neon-green font-semibold">« Sur l'écran d'accueil »</span>.
          </p>
        </div>
        <button data-testid="pwa-ios-dismiss" onClick={dismiss} className="shrink-0 text-zinc-500 hover:text-white"><X className="h-4 w-4" /></button>
      </div>
    );
  }
  return null;
};

export const InstallFooterCard = () => {
  if (isStandalone()) return null;
  return (
    <div data-testid="footer-install-card" className="text-zinc-500 text-xs max-w-sm space-y-2">
      <p className="text-neon-green font-semibold uppercase text-xs">📲 Installe l'app</p>
      <div className="flex items-start gap-3 border border-zinc-800 bg-ink-surface p-3">
        <img src="/icon-192.png" alt="STUFF CBD" className="h-10 w-10 shrink-0" loading="lazy" />
        <div className="space-y-1.5">
          <p><span className="text-white font-semibold">iPhone :</span> dans Safari, appuie sur <Share className="inline h-3 w-3 text-neon-green -mt-0.5" /> Partager → <span className="text-zinc-300">« Sur l'écran d'accueil »</span></p>
          <p><span className="text-white font-semibold">Android :</span> menu ⋮ de Chrome → <span className="text-zinc-300">« Installer l'application »</span></p>
          <p className="text-zinc-600">Commande ton CBD en 2 étapes, sans passer par un store.</p>
        </div>
      </div>
    </div>
  );
};
